import { useCallback, useState } from 'react';
import { getIdToken } from '@helios/auth-firebase-web';

/* ------------------------------------------------------------------ */
/*  useSendWhatsAppBookingFlow (PRD.258)                              */
/*                                                                    */
/*  Thin client for the `psendWhatsAppBookingFlow` Cloud Function.    */
/*  Creates a whatsapp_flow_sessions row + sends the approved booking */
/*  template with a Flow button. Uses an approved template, so it     */
/*  works outside the 24-hour window (no eligibility preflight).      */
/* ------------------------------------------------------------------ */

const BOOKING_FLOW_URL = import.meta.env.VITE_WHATSAPP_BOOKING_FLOW_URL;

export type BookingFlowStatus = 'idle' | 'sending' | 'success' | 'error';

export interface SendBookingFlowInput {
  /** WhatsApp destination phone (raw or E.164). */
  to: string;
  patientId?: string;
  clinicId?: string;
  providerId?: string;
  serviceId?: string;
  clientReference?: string;
}

export interface SendBookingFlowResult {
  flowSessionId: string;
  reusedSession?: boolean;
  accepted?: boolean;
  providerMessageId?: string;
}

export interface UseSendWhatsAppBookingFlowReturn {
  status: BookingFlowStatus;
  error: string | null;
  lastResult: SendBookingFlowResult | null;
  /** True when the endpoint URL is configured for this client. */
  enabled: boolean;
  send: (input: SendBookingFlowInput) => Promise<SendBookingFlowResult | null>;
  reset: () => void;
}

export function useSendWhatsAppBookingFlow(): UseSendWhatsAppBookingFlowReturn {
  const [status, setStatus] = useState<BookingFlowStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<SendBookingFlowResult | null>(null);

  const enabled = Boolean(BOOKING_FLOW_URL);

  const send = useCallback(
    async (input: SendBookingFlowInput): Promise<SendBookingFlowResult | null> => {
      if (!BOOKING_FLOW_URL) {
        setError('Booking Flow WhatsApp endpoint is not configured for this client.');
        setStatus('error');
        return null;
      }
      setStatus('sending');
      setError(null);
      try {
        const token = await getIdToken();
        const res = await fetch(BOOKING_FLOW_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            to: input.to,
            patientId: input.patientId,
            clinicId: input.clinicId,
            providerId: input.providerId,
            serviceId: input.serviceId,
            clientReference: input.clientReference,
            source: 'WEB_ADMIN',
          }),
        });
        const body: unknown = await res.json().catch(() => null);

        if (!res.ok) {
          setError(friendlyError(res.status, body));
          setStatus('error');
          return null;
        }

        const result = body as SendBookingFlowResult;
        setLastResult(result);
        setStatus('success');
        return result;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to send the booking message.';
        setError(msg);
        setStatus('error');
        return null;
      }
    },
    [],
  );

  const reset = useCallback(() => {
    setStatus('idle');
    setError(null);
    setLastResult(null);
  }, []);

  return { status, error, lastResult, enabled, send, reset };
}

function extractError(body: unknown): string {
  if (typeof body === 'object' && body !== null && 'error' in body) {
    return String((body as { error?: unknown }).error ?? '');
  }
  return '';
}

function friendlyError(httpStatus: number, body: unknown): string {
  const raw = extractError(body);
  switch (httpStatus) {
    case 400:
      return raw || 'The request is invalid. Please review and try again.';
    case 401:
      return 'Your session expired. Please sign in again.';
    case 403:
      return raw || 'You are not allowed to send the booking message for this clinic.';
    case 422:
      return raw || 'The booking template is not configured. Please contact support.';
    case 502:
      return raw || 'WhatsApp could not accept the booking message. Please try again.';
    default:
      return raw || `Failed to send the booking message (HTTP ${httpStatus}).`;
  }
}
